import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardCheck, Edit, Eye, FolderOpen, Plus, Search, Trash2, Users, Video } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, difficultyClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = () => api.get("/admin/tutorials").then((r) => setTutorials(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (tutorials || []).filter((t) =>
    (status === "all" || t.status === status) && t.title.toLowerCase().includes(search.toLowerCase())), [tutorials, search, status]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/tutorials/${pendingDelete.id}`);
      toast.success("Tutorial deleted");
      setPendingDelete(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const togglePublish = async (t) => {
    try {
      const full = (await api.get(`/tutorials/${t.id}`)).data;
      const payload = { title: full.title, summary: full.summary, category_id: full.category_id, difficulty: full.difficulty, estimated_minutes: full.estimated_minutes, cover_image: full.cover_image, audience: full.audience || "all", steps: full.steps, status: t.status === "published" ? "draft" : "published" };
      await api.put(`/admin/tutorials/${t.id}`, payload);
      toast.success(payload.status === "published" ? "Tutorial published" : "Moved to drafts");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div className="space-y-2">
          <p className="eyebrow">Admin panel</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Tutorials</h1>
          <p className="text-slate-600">Create, edit and publish guides and exercises. Clients only see published tutorials.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" asChild><Link to="/admin/videos" data-testid="admin-videos-link"><Video className="w-4 h-4" /> Demo videos</Link></Button>
          <Button variant="outline" className="rounded-full" asChild><Link to="/admin/applications" data-testid="admin-applications-link"><ClipboardCheck className="w-4 h-4" /> Applications</Link></Button>
          <Button variant="outline" className="rounded-full" asChild><Link to="/admin/users" data-testid="admin-users-link"><Users className="w-4 h-4" /> Accounts</Link></Button>
          <Button variant="outline" className="rounded-full" asChild><Link to="/admin/categories" data-testid="admin-categories-link"><FolderOpen className="w-4 h-4" /> Categories</Link></Button>
          <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate("/admin/tutorials/new")} data-testid="admin-add-tutorial-button"><Plus className="w-4 h-4" /> New tutorial</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title" className="h-11 pl-11 rounded-full bg-white" data-testid="admin-search-input" />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          {["all", "published", "draft"].map((s) => (
            <button key={s} type="button" onClick={() => setStatus(s)} data-testid={`admin-status-filter-${s}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${status === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{s}</button>
          ))}
        </div>
      </div>

      {!tutorials ? <Skeleton className="h-64 rounded-3xl" /> : (
        <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-6 py-4 font-semibold">Title</th><th className="px-6 py-4 font-semibold hidden md:table-cell">Category</th><th className="px-6 py-4 font-semibold hidden sm:table-cell">Level</th><th className="px-6 py-4 font-semibold hidden sm:table-cell">Steps</th><th className="px-6 py-4 font-semibold">Status</th><th className="px-6 py-4 font-semibold text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="admin-tutorials-table">
              {filtered.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500" data-testid="admin-empty">No tutorials found.</td></tr>}
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60" data-testid={`admin-row-${t.id}`}>
                  <td className="px-6 py-4 font-medium text-slate-900 max-w-xs truncate">{t.title}</td>
                  <td className="px-6 py-4 text-slate-600 hidden md:table-cell">{t.category_name}</td>
                  <td className="px-6 py-4 hidden sm:table-cell"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${difficultyClass[t.difficulty]}`}>{t.difficulty}</span></td>
                  <td className="px-6 py-4 text-slate-600 hidden sm:table-cell">{t.step_count}</td>
                  <td className="px-6 py-4">
                    <button type="button" onClick={() => togglePublish(t)} data-testid={`admin-toggle-status-${t.id}`}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${t.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"}`}>
                      {t.status}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" asChild aria-label="Preview"><Link to={`/tutorials/${t.id}`} data-testid={`admin-preview-${t.id}`}><Eye className="w-4 h-4" /></Link></Button>
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/admin/tutorials/${t.id}`)} aria-label="Edit" data-testid={`admin-edit-${t.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setPendingDelete(t)} aria-label="Delete" data-testid={`admin-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{pendingDelete?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>This removes the tutorial and every client's progress on it. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
